import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from './icons';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
}

/**
 * Bottom sheet on phones, centered dialog on wider screens.
 * Escape and backdrop close it; focus moves in on open, is trapped while open and restored on close.
 */
export function Sheet({ open, onClose, title, description, children }: SheetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const prevFocus = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // Focus the first [data-autofocus] element (user just tapped to open, so the keyboard is expected), else the panel.
    const target = panel?.querySelector<HTMLElement>('[data-autofocus]') ?? panel;
    target?.focus({ preventScroll: true });
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      } else if (e.key === 'Tab' && panel) {
        const f = panel.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])',
        );
        if (!f.length) return;
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-40 flex items-end justify-center sm:items-center sm:p-6">
      <div className="fade-in absolute inset-0 bg-scrim backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="sheet-in relative max-h-[88dvh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-surface px-5 pt-2 pb-[calc(var(--safe-bottom)+1.25rem)] shadow-lg focus:outline-none sm:rounded-3xl sm:pb-6"
      >
        <div className="mx-auto mt-1 mb-3 h-1.5 w-10 rounded-full bg-surface-3 sm:hidden" aria-hidden="true" />
        <div className="mb-4 flex items-start gap-3">
          <div className="min-w-0 flex-1 pt-2">
            <h2 id={titleId} className="text-lg leading-tight font-bold break-words text-ink">
              {title}
            </h2>
            {description && <div className="mt-1 text-[0.95rem] text-muted">{description}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="tap -mr-2 grid place-items-center rounded-full text-muted hover:bg-surface-2"
          >
            <IconX />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

export function SheetAction({
  icon,
  children,
  onClick,
  tone = 'default',
  disabled,
}: {
  icon: ReactNode;
  children: ReactNode;
  onClick: () => void;
  tone?: 'default' | 'danger';
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex min-h-14 w-full items-center gap-4 rounded-2xl px-3 text-left text-base font-medium transition-colors hover:bg-surface-2 active:bg-surface-3 disabled:opacity-50 ${
        tone === 'danger' ? 'text-danger' : 'text-ink'
      }`}
    >
      <span
        className={`grid size-10 shrink-0 place-items-center rounded-xl ${
          tone === 'danger' ? 'bg-danger-soft' : 'bg-surface-2'
        }`}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="flex-1">{children}</span>
    </button>
  );
}
